import { useEffect, useMemo, useState } from "react";
import {
  Copy,
  Edit3,
  Eye,
  LayoutDashboard,
  Monitor,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Trophy,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  adminLeaderboardWidgets,
  fetchLeaderboardServers,
  fetchLeaderboardSeasons,
  fetchLeaderboardWidget,
} from "@/lib/api";

const LAYOUTS = [
  { value: "table", label: "Table" },
  { value: "compact", label: "Compact" },
  { value: "podium", label: "Podium" },
  { value: "dashboard", label: "Dashboard" },
  { value: "player-card", label: "Player Cards" },
  { value: "event", label: "Event Leaderboard" },
];

const CATEGORIES = [
  { value: "overall", label: "Overall" },
  { value: "pvp", label: "PvP" },
  { value: "kills", label: "Kills" },
  { value: "deaths", label: "Deaths" },
  { value: "damage_dealt", label: "Damage Dealt" },
  { value: "damage_taken", label: "Damage Taken" },
  { value: "blocks_mined", label: "Blocks Mined" },
  { value: "jumps", label: "Jumps" },
  { value: "playtime", label: "Playtime" },
  { value: "distance", label: "Distance" },
  { value: "money", label: "Money" },
];

const DEFAULT_CONFIG = {
  limit: 10,
  show_rank: true,
  show_avatar: true,
  show_kills: true,
  show_deaths: true,
  show_kd: true,
  show_event_points: true,
  show_rating: true,
  search: true,
};

const emptyForm = {
  name: "",
  slug: "",
  server_id: "",
  season_id: "",
  category: "overall",
  layout: "table",
  title: "",
  subtitle: "",
  config: DEFAULT_CONFIG,
  active: true,
  display_order: 0,
};

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function Field({ label, children, hint }) {
  return (
    <label className="block space-y-2">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
        {label}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-white/35">{hint}</span>}
    </label>
  );
}

const inputClass =
  "w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white outline-none transition focus:border-[#FFBF00]/60";

function Toggle({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex items-center justify-between gap-4 rounded-xl border px-3 py-2.5 text-left transition ${
        checked
          ? "border-[#FFBF00]/30 bg-[#FFBF00]/10"
          : "border-white/10 bg-black/20"
      }`}
    >
      <span className="text-xs text-white/80">{label}</span>
      <span
        className={`relative h-5 w-9 rounded-full transition ${
          checked ? "bg-[#FFBF00]" : "bg-white/15"
        }`}
      >
        <span
          className={`absolute top-1 h-3 w-3 rounded-full bg-white transition ${
            checked ? "left-5" : "left-1"
          }`}
        />
      </span>
    </button>
  );
}

function WidgetPreview({ widget, preview, loading }) {
  if (!widget) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-white/35">
        Select a widget to preview it.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/20 p-8 text-center text-sm text-white/45">
        Loading live leaderboard…
      </div>
    );
  }

  const entries = preview?.entries || [];

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#090909]">
      <div className="border-b border-white/10 bg-white/[0.025] p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-[0.25em] text-[#FFBF00]">
              {preview?.server?.name || widget.server_name}
            </div>
            <h3 className="text-lg font-bold text-white">
              {widget.title || widget.name}
            </h3>
            {widget.subtitle && (
              <p className="mt-1 text-xs text-white/45">{widget.subtitle}</p>
            )}
          </div>

          <div className="rounded-lg border border-white/10 px-3 py-2 text-right">
            <div className="text-lg font-bold text-white">
              {preview?.total_players ?? 0}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-white/35">
              Players
            </div>
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="p-8 text-center text-sm text-white/40">
          No leaderboard entries.
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {entries.map((player, index) => (
            <div
              key={`${player.id}-${player.name}-${index}`}
              className="flex items-center gap-3 px-4 py-3"
            >
              <div className="w-7 text-center text-xs font-bold text-white/35">
                {index + 1}
              </div>

              <div className="flex min-w-0 flex-1 items-center gap-3">
                {widget.config?.show_avatar !== false && (
                  <img
                    src={`https://mc-heads.net/avatar/${encodeURIComponent(
                      player.username || player.name
                    )}/40`}
                    alt=""
                    className="h-9 w-9 rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}

                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-white">
                    {player.name}
                  </div>
                  <div className="truncate text-[10px] text-white/35">
                    {player.prefix || player.tag || "PLAYER"}
                  </div>
                </div>
              </div>

              <div className="hidden items-center gap-5 text-right sm:flex">
                {widget.config?.show_kills !== false && (
                  <div>
                    <div className="text-xs font-semibold text-white">
                      {player.kills}
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/30">
                      Kills
                    </div>
                  </div>
                )}

                {widget.config?.show_deaths !== false && (
                  <div>
                    <div className="text-xs font-semibold text-white">
                      {player.deaths}
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/30">
                      Deaths
                    </div>
                  </div>
                )}

                {widget.config?.show_kd !== false && (
                  <div>
                    <div className="text-xs font-semibold text-white">
                      {player.kd}
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/30">
                      K/D
                    </div>
                  </div>
                )}

                {widget.config?.show_event_points && (
                  <div>
                    <div className="text-xs font-semibold text-[#FFBF00]">
                      {player.event_points || 0}
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/30">
                      Event
                    </div>
                  </div>
                )}

                {widget.config?.show_rating !== false && (
                  <div>
                    <div className="text-sm font-black text-white">
                      {player.rating}
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/30">
                      Rating
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLeaderboardWidgets() {
  const [widgets, setWidgets] = useState([]);
  const [servers, setServers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const filteredWidgets = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return widgets;

    return widgets.filter((w) =>
      [w.name, w.slug, w.server_name, w.season_name, w.layout, w.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [widgets, filter]);

  async function load() {
    setLoading(true);
    try {
      const [widgetData, serverData] = await Promise.all([
        adminLeaderboardWidgets.list(),
        fetchLeaderboardServers(),
      ]);

      setWidgets(Array.isArray(widgetData) ? widgetData : []);
      setServers(Array.isArray(serverData) ? serverData : []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load leaderboard widgets.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function loadSeasons(serverId) {
    const server = servers.find((item) => Number(item.id) === Number(serverId));

    if (!server) {
      setSeasons([]);
      return;
    }

    try {
      const data = await fetchLeaderboardSeasons(server.slug);
      setSeasons(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setSeasons([]);
      toast.error("Failed to load seasons.");
    }
  }

  function newWidget() {
    setSelected(null);
    setSeasons([]);
    setForm({
      ...emptyForm,
      config: { ...DEFAULT_CONFIG },
    });
  }

  async function editWidget(widget) {
    setSelected(widget);
    setForm({
      name: widget.name || "",
      slug: widget.slug || "",
      server_id: widget.server_id || "",
      season_id: widget.season_id || "",
      category: widget.category || "overall",
      layout: widget.layout || "table",
      title: widget.title || "",
      subtitle: widget.subtitle || "",
      config: {
        ...DEFAULT_CONFIG,
        ...(widget.config || {}),
      },
      active: !!widget.active,
      display_order: widget.display_order || 0,
    });

    await loadSeasons(widget.server_id);
    await previewWidget(widget.slug);
  }

  async function previewWidget(slug) {
    if (!slug) return;

    setPreviewLoading(true);

    try {
      const data = await fetchLeaderboardWidget(slug);
      setPreview(data);
    } catch (error) {
      console.error(error);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  function changeServer(value) {
    setForm((current) => ({
      ...current,
      server_id: value,
      season_id: "",
    }));
    loadSeasons(value);
  }

  function updateConfig(key, value) {
    setForm((current) => ({
      ...current,
      config: {
        ...current.config,
        [key]: value,
      },
    }));
  }

  async function save() {
    if (!form) return;

    if (!form.name.trim()) {
      toast.error("Widget name is required.");
      return;
    }

    if (!form.slug.trim()) {
      toast.error("Widget slug is required.");
      return;
    }

    if (!form.server_id || !form.season_id) {
      toast.error("Select a server and season.");
      return;
    }

    const payload = {
      ...form,
      server_id: Number(form.server_id),
      season_id: Number(form.season_id),
      display_order: Number(form.display_order || 0),
      config: {
        ...form.config,
        limit: Math.max(1, Math.min(100, Number(form.config.limit || 10))),
      },
    };

    setSaving(true);

    try {
      if (selected?.id) {
        await adminLeaderboardWidgets.update(selected.id, payload);
        toast.success("Widget updated.");
      } else {
        await adminLeaderboardWidgets.create(payload);
        toast.success("Widget created.");
      }

      setForm(null);
      setSelected(null);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || "Failed to save widget."
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeWidget(widget) {
    if (!window.confirm(`Delete widget "${widget.name}"?`)) return;

    try {
      await adminLeaderboardWidgets.remove(widget.id);
      toast.success("Widget deleted.");
      if (selected?.id === widget.id) {
        setSelected(null);
        setForm(null);
      }
      await load();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || "Failed to delete widget."
      );
    }
  }

  async function toggleActive(widget) {
    try {
      await adminLeaderboardWidgets.update(widget.id, {
        name: widget.name,
        slug: widget.slug,
        server_id: widget.server_id,
        season_id: widget.season_id,
        category: widget.category,
        layout: widget.layout,
        title: widget.title || "",
        subtitle: widget.subtitle || "",
        config: widget.config || DEFAULT_CONFIG,
        active: !widget.active,
        display_order: widget.display_order || 0,
      });

      toast.success(widget.active ? "Widget disabled." : "Widget enabled.");
      await load();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update widget status.");
    }
  }

  function copyEmbed(widget) {
    const code = `<iframe src="${window.location.origin}/leaderboard/embed/${widget.slug}" width="100%" height="600" frameborder="0" loading="lazy"></iframe>`;

    navigator.clipboard
      .writeText(code)
      .then(() => toast.success("Embed code copied."))
      .catch(() => toast.error("Could not copy embed code."));
  }

  return (
    <div className="space-y-6" data-testid="admin-leaderboard-widgets">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#FFBF00]">
            <Trophy size={13} />
            Leaderboard System
          </div>
          <h2 className="text-2xl font-black text-white">Widgets</h2>
          <p className="mt-1 text-sm text-white/45">
            Create reusable leaderboard blocks for your website.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs uppercase tracking-widest text-white/70 hover:border-white/20"
          >
            <RefreshCw size={13} />
            Refresh
          </button>

          <button
            type="button"
            onClick={newWidget}
            className="inline-flex items-center gap-2 rounded-full bg-[#FFBF00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-black hover:bg-[#FFD24D]"
          >
            <Plus size={14} />
            New Widget
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
        <Search size={16} className="text-white/35" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search widgets..."
          className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/25"
        />
        <span className="whitespace-nowrap text-[10px] uppercase tracking-widest text-white/25">
          {filteredWidgets.length} widgets
        </span>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-white/10 p-10 text-center text-sm text-white/40">
          Loading widgets…
        </div>
      ) : filteredWidgets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
          <LayoutDashboard className="mx-auto mb-3 text-white/20" size={32} />
          <div className="text-sm font-semibold text-white/60">
            No widgets found
          </div>
          <p className="mt-1 text-xs text-white/30">
            Create your first reusable leaderboard widget.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredWidgets.map((widget) => (
            <div
              key={widget.id}
              className="rounded-2xl border border-white/10 bg-white/[0.025] p-4"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FFBF00]/10 text-[#FFBF00]">
                    <LayoutDashboard size={19} />
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-bold text-white">
                        {widget.name}
                      </h3>

                      <span
                        className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest ${
                          widget.active
                            ? "bg-emerald-400/10 text-emerald-300"
                            : "bg-white/5 text-white/35"
                        }`}
                      >
                        {widget.active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/35">
                      <span>{widget.slug}</span>
                      <span>{widget.server_name}</span>
                      <span>{widget.season_name}</span>
                      <span>{widget.layout}</span>
                      <span>{widget.category}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => previewWidget(widget.slug)}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/65 hover:border-white/20"
                  >
                    <Eye size={13} />
                    Preview
                  </button>

                  <button
                    type="button"
                    onClick={() => copyEmbed(widget)}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/65 hover:border-white/20"
                  >
                    <Copy size={13} />
                    Embed
                  </button>

                  <button
                    type="button"
                    onClick={() => toggleActive(widget)}
                    className="rounded-lg border border-white/10 px-3 py-2 text-xs text-white/65 hover:border-white/20"
                  >
                    {widget.active ? "Disable" : "Enable"}
                  </button>

                  <button
                    type="button"
                    onClick={() => editWidget(widget)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs text-white hover:bg-white/15"
                  >
                    <Edit3 size={13} />
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => removeWidget(widget)}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-400/10 px-3 py-2 text-xs text-red-300/70 hover:border-red-400/30 hover:text-red-300"
                  >
                    <Trash2 size={13} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-white">Live Preview</h3>
              <p className="text-xs text-white/35">
                Previewing {preview.widget?.slug}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="rounded-full border border-white/10 p-2 text-white/50 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>

          <WidgetPreview
            widget={preview.widget}
            preview={preview}
            loading={previewLoading}
          />
        </section>
      )}

      {form && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/75 px-4 py-10 backdrop-blur-md">
          <div className="mx-auto max-w-5xl rounded-3xl border border-white/10 bg-[#101010] shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFBF00]">
                  {selected ? "Edit Widget" : "Create Widget"}
                </div>
                <h2 className="mt-1 text-xl font-black text-white">
                  {selected ? selected.name : "New Leaderboard Widget"}
                </h2>
              </div>

              <button
                type="button"
                onClick={() => setForm(null)}
                className="rounded-full border border-white/10 p-2 text-white/50 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid gap-6 p-6 lg:grid-cols-2">
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name">
                    <input
                      className={inputClass}
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      placeholder="Season 8 Top 10"
                    />
                  </Field>

                  <Field label="Slug" hint="Lowercase letters, numbers and hyphens.">
                    <input
                      className={inputClass}
                      value={form.slug}
                      onChange={(e) =>
                        setForm({ ...form, slug: slugify(e.target.value) })
                      }
                      placeholder="s8-top10"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Server">
                    <select
                      className={inputClass}
                      value={form.server_id}
                      onChange={(e) => changeServer(e.target.value)}
                    >
                      <option value="">Select server</option>
                      {servers.map((server) => (
                        <option key={server.id} value={server.id}>
                          {server.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Season">
                    <select
                      className={inputClass}
                      value={form.season_id}
                      onChange={(e) =>
                        setForm({ ...form, season_id: e.target.value })
                      }
                      disabled={!form.server_id}
                    >
                      <option value="">Select season</option>
                      {seasons.map((season) => (
                        <option key={season.id} value={season.id}>
                          {season.name} — {season.database_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Category">
                    <select
                      className={inputClass}
                      value={form.category}
                      onChange={(e) =>
                        setForm({ ...form, category: e.target.value })
                      }
                    >
                      {CATEGORIES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Layout">
                    <select
                      className={inputClass}
                      value={form.layout}
                      onChange={(e) =>
                        setForm({ ...form, layout: e.target.value })
                      }
                    >
                      {LAYOUTS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Title">
                  <input
                    className={inputClass}
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    placeholder="LostDC SMP Season 8"
                  />
                </Field>

                <Field label="Subtitle">
                  <input
                    className={inputClass}
                    value={form.subtitle}
                    onChange={(e) =>
                      setForm({ ...form, subtitle: e.target.value })
                    }
                    placeholder="Top 10 Players"
                  />
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Player Limit">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className={inputClass}
                      value={form.config.limit}
                      onChange={(e) =>
                        updateConfig("limit", Number(e.target.value))
                      }
                    />
                  </Field>

                  <Field label="Display Order">
                    <input
                      type="number"
                      className={inputClass}
                      value={form.display_order}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          display_order: Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                </div>

                <Toggle
                  label="Widget active"
                  checked={!!form.active}
                  onChange={() =>
                    setForm({ ...form, active: !form.active })
                  }
                />
              </div>

              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Monitor size={15} className="text-[#FFBF00]" />
                  <h3 className="text-sm font-bold text-white">
                    Display Fields
                  </h3>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <Toggle
                    label="Rank"
                    checked={!!form.config.show_rank}
                    onChange={() =>
                      updateConfig("show_rank", !form.config.show_rank)
                    }
                  />
                  <Toggle
                    label="Avatar"
                    checked={!!form.config.show_avatar}
                    onChange={() =>
                      updateConfig("show_avatar", !form.config.show_avatar)
                    }
                  />
                  <Toggle
                    label="Kills"
                    checked={!!form.config.show_kills}
                    onChange={() =>
                      updateConfig("show_kills", !form.config.show_kills)
                    }
                  />
                  <Toggle
                    label="Deaths"
                    checked={!!form.config.show_deaths}
                    onChange={() =>
                      updateConfig("show_deaths", !form.config.show_deaths)
                    }
                  />
                  <Toggle
                    label="K/D"
                    checked={!!form.config.show_kd}
                    onChange={() =>
                      updateConfig("show_kd", !form.config.show_kd)
                    }
                  />
                  <Toggle
                    label="Event Points"
                    checked={!!form.config.show_event_points}
                    onChange={() =>
                      updateConfig(
                        "show_event_points",
                        !form.config.show_event_points
                      )
                    }
                  />
                  <Toggle
                    label="Rating"
                    checked={!!form.config.show_rating}
                    onChange={() =>
                      updateConfig("show_rating", !form.config.show_rating)
                    }
                  />
                  <Toggle
                    label="Search"
                    checked={!!form.config.search}
                    onChange={() =>
                      updateConfig("search", !form.config.search)
                    }
                  />
                </div>

                <div className="mt-5">
                  <div className="mb-3 text-[10px] uppercase tracking-[0.22em] text-white/35">
                    Embed Preview
                  </div>

                  <WidgetPreview
                    widget={form}
                    preview={{
                      widget: form,
                      total_players: 0,
                      entries: [],
                    }}
                    loading={false}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-white/10 px-6 py-5">
              <button
                type="button"
                onClick={() => setForm(null)}
                className="rounded-full border border-white/10 px-5 py-2.5 text-xs uppercase tracking-widest text-white/60 hover:text-white"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-[#FFBF00] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-black hover:bg-[#FFD24D] disabled:opacity-50"
              >
                <Save size={14} />
                {saving ? "Saving..." : selected ? "Save Changes" : "Create Widget"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
